import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getAllBanners } from "../../services/HomePage/BannerApi";

import { FaChevronLeft, FaChevronRight, FaFire, FaStar } from "react-icons/fa";

import useEmblaCarousel from "embla-carousel-react";

export default function SpecializedBanner() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imgErrors, setImgErrors] = useState({});
  const [isReady, setIsReady] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const navigate = useNavigate();
  const autoplayRef = useRef(null);

  const BASE_IMAGE_URL = process.env.REACT_APP_BANNER_IMAGE_URL || "https://admintest.medrphalabs.com/BannerImage";

  // ✅ Embla Carousel - No loop
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: "start",
      containScroll: "trimSnaps",
      loop: false,
      dragFree: false,
      slidesToScroll: 1,
      speed: 10,
      breakpoints: {
        '(min-width: 768px)': { slidesToScroll: 1, align: "start" },
        '(min-width: 1024px)': { slidesToScroll: 1, align: "start" }
      }
    }
  );

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  // ✅ Update current index on slide change
  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setCurrentIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on("select", onSelect);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  // ✅ Auto-slide function - stops at end
  const startAutoplay = useCallback(() => {
    if (!emblaApi || !isReady || banners.length === 0) return;

    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
    }

    autoplayRef.current = setInterval(() => {
      if (emblaApi) {
        const current = emblaApi.selectedScrollSnap();
        const total = emblaApi.scrollSnapList().length - 1;
        
        if (current >= total) {
          clearInterval(autoplayRef.current);
          autoplayRef.current = null;
          return;
        }
        
        emblaApi.scrollNext();
      }
    }, 3000);
  }, [emblaApi, isReady, banners]);

  // ✅ Start autoplay when ready
  useEffect(() => {
    if (emblaApi && isReady && banners.length > 0) {
      setTimeout(() => {
        emblaApi.reInit();
        startAutoplay();
      }, 300);
    }
  }, [emblaApi, isReady, banners, startAutoplay]);

  // ✅ Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
        autoplayRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchBanners = async () => {
      try {
        setLoading(true);
        const data = await getAllBanners(2);
        const list = Array.isArray(data) ? data : [];
        const activeBanners = list
          .filter((item) => item.isActive !== false && item.image && String(item.image).trim() !== "")
          .map((item) => {
            const raw = String(item.image).trim();
            let fullUrl = "";
            if (raw.startsWith("http")) {
              fullUrl = raw;
            } else if (raw.startsWith("BannerImage/")) {
              fullUrl = `https://admin.medrphalabs.com/${raw}`;
            } else {
              fullUrl = `https://admin.medrphalabs.com/BannerImage/${raw}`;
            }

            return {
              ...item,
              resolvedImageUrl: fullUrl,
            };
          });

        if (isMounted) {
          const uniqueBanners = [];
          const seenIds = new Set();
          activeBanners.forEach((item) => {
            const id = item.id || item.resolvedImageUrl;
            if (!seenIds.has(id)) {
              seenIds.add(id);
              uniqueBanners.push(item);
            }
          });
          setBanners(uniqueBanners);
          setIsReady(true);
        }
      } catch (error) {
        console.error("Fetch Error:", error);
        if (isMounted) setBanners([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchBanners();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleBannerClick = (item, e) => {
    e.stopPropagation();
    if (Number(item.productCategoryId) === 0) return;
    navigate(`/brand/${item.productCategoryId}`);
  };

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 bg-white rounded-2xl border border-slate-200/80 shadow-xs my-6">
        <div className="flex justify-center gap-4 overflow-hidden">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="w-[240px] sm:w-[280px] h-[180px] sm:h-[200px] md:h-[220px] bg-slate-100 rounded-2xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  const validBanners = banners.filter((item, i) => !imgErrors[i]);

  if (validBanners.length === 0) {
    return null;
  }

  return (
    <div className="px-4 md:px-8 py-6 bg-white rounded-3xl border border-slate-200/80 shadow-xs my-6">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2.5">
          <div className="w-2 h-7 bg-gradient-to-b from-red-600 to-rose-600 rounded-full" />
          <div>
            <h3 className="text-lg sm:text-xl md:text-2xl font-black text-slate-900 leading-tight">
              Exclusive Offers & Collections
            </h3>
            <p className="text-xs text-slate-500 font-medium mt-0.5">
              Discover our special deals and limited time offers ({validBanners.length} available)
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto">
          {/* Left Arrow */}
          <button
            onClick={scrollPrev}
            className="w-9 h-9 rounded-full bg-slate-100 hover:bg-red-600 hover:text-white text-slate-700 flex items-center justify-center transition-all shadow-2xs cursor-pointer"
            aria-label="Previous slide"
          >
            <FaChevronLeft size={14} />
          </button>

          {/* Right Arrow */}
          <button
            onClick={scrollNext}
            className="w-9 h-9 rounded-full bg-slate-100 hover:bg-red-600 hover:text-white text-slate-700 flex items-center justify-center transition-all shadow-2xs cursor-pointer"
            aria-label="Next slide"
          >
            <FaChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Carousel with matching dimensions */}
      <div className="relative w-full">
        <div className="overflow-hidden w-full" ref={emblaRef}>
          <div className="flex gap-4">
            {banners.map((item, i) => {
              if (imgErrors[i]) return null;
              const imgUrl = item.resolvedImageUrl;

              return (
                <div
                  key={item.id || i}
                  className="min-w-[260px] sm:min-w-[300px] md:min-w-[340px] flex-shrink-0"
                >
                  <div
                    onClick={(e) => handleBannerClick(item, e)}
                    className={`relative w-full h-[150px] sm:h-[175px] md:h-[195px] 
                    rounded-2xl overflow-hidden shadow-2xs hover:shadow-md border border-slate-200/90
                    transition-all duration-300 bg-white flex items-center justify-center group
                    ${Number(item.productCategoryId) === 0 ? 'cursor-default' : 'cursor-pointer'}`}
                  >
                    <img
                      src={imgUrl}
                      alt={item.title || "Exclusive Offer Banner"}
                      className="w-full h-full object-fill group-hover:scale-105 transition-transform duration-300 rounded-2xl"
                      onError={() => setImgErrors(prev => ({ ...prev, [i]: true }))}
                      loading="lazy"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
}